import type { InitialJsonState, CurrentJsonState, ProposalForm, ProposalValues, CurrentFieldState } from "./types";

const numberToString = (value: number): string => value.toString();
const rationalToString = (value: [number, number]): string => `${value[0]}/${value[1]}`;
const stringToNumber = (value: string): number => parseInt(value);
const stringToRational = (value: string): [number, number] => value.split('/').map(v => parseInt(v)) as [number, number];
const numberToCurrentFieldState = (value: number): CurrentFieldState => ({ value: value.toString(), checkStatus: 'unchecked' });
const rationalToCurrentFieldState = (value: [number, number]): CurrentFieldState => ({ value: `${value[0]}/${value[1]}`, checkStatus: 'unchecked' });
const currentFieldStateToNumber = (value: CurrentFieldState): number => parseInt(value.value);
const currentFieldStateToRational = (value: CurrentFieldState): [number, number] => value.value.split('/').map(v => parseInt(v)) as [number, number];

export const mapInitialJsonStateToProposalForm = (values: InitialJsonState): ProposalForm => ({
  txFeePerByte: numberToString(values[0]),
  txFeeFixed: numberToString(values[1]),
  maxBlockBodySize: numberToString(values[2]),
  maxTxSize: numberToString(values[3]),
  maxBlockHeaderSize: numberToString(values[4]),
  stakeAddressDeposit: numberToString(values[5]),
  stakePoolDeposit: numberToString(values[6]),
  poolRetireMaxEpoch: numberToString(values[7]),
  stakePoolTargetNum: numberToString(values[8]),
  poolPledgeInfluence: rationalToString(values[9]),
  monetaryExpansion: rationalToString(values[10]),
  treasuryCut: rationalToString(values[11]),
  minPoolCost: numberToString(values[16]),
  utxoCostPerByte: numberToString(values[17]),
  maxValueSize: numberToString(values[22]),
  collateralPercentage: numberToString(values[23]),
  maxCollateralInputs: numberToString(values[24]),
  committeeMinSize: numberToString(values[27]),
  committeeMaxTermLimit: numberToString(values[28]),
  govActionLifetime: numberToString(values[29]),
  govDeposit: numberToString(values[30]),
  dRepDeposit: numberToString(values[31]),
  dRepActivity: numberToString(values[32]),
  minFeeRefScriptCoinsPerByte: numberToString(values[33]),
  executionUnitPrices: {
    priceMemory: rationalToString(values[19].priceMemory),
    priceSteps: rationalToString(values[19].priceSteps),
  },
  maxTxExecutionUnits: {
    mem: numberToString(values[20].mem),
    steps: numberToString(values[20].steps),
  },
  maxBlockExecutionUnits: {
    memory: numberToString(values[21].memory),
    steps: numberToString(values[21].steps),
  },
  poolVotingThresholds: {
    committeeNoConfidence: rationalToString(values[25].committeeNoConfidence),
    committeeNormal: rationalToString(values[25].committeeNormal),
    hardForkInitiation: rationalToString(values[25].hardForkInitiation),
    motionNoConfidence: rationalToString(values[25].motionNoConfidence),
    ppSecurityGroup: rationalToString(values[25].ppSecurityGroup),
  },
  dRepVotingThresholds: {
    committeeNoConfidence: rationalToString(values[26].committeeNoConfidence),
    committeeNormal: rationalToString(values[26].committeeNormal),
    hardForkInitiation: rationalToString(values[26].hardForkInitiation),
    motionNoConfidence: rationalToString(values[26].motionNoConfidence),
    ppEconomicGroup: rationalToString(values[26].ppEconomicGroup),
    ppGovernanceGroup: rationalToString(values[26].ppGovernanceGroup),
    ppNetworkGroup: rationalToString(values[26].ppNetworkGroup),
    ppTechnicalGroup: rationalToString(values[26].ppTechnicalGroup),
    treasuryWithdrawal: rationalToString(values[26].treasuryWithdrawal),
    updateConstitution: rationalToString(values[26].updateConstitution),
  },
});

export const mapProposalFormToProposalValues = (form: ProposalForm): ProposalValues => ({
  0: stringToNumber(form.txFeePerByte),
  1: stringToNumber(form.txFeeFixed),
  2: stringToNumber(form.maxBlockBodySize),
  3: stringToNumber(form.maxTxSize),
  4: stringToNumber(form.maxBlockHeaderSize),
  5: stringToNumber(form.stakeAddressDeposit),
  6: stringToNumber(form.stakePoolDeposit),
  7: stringToNumber(form.poolRetireMaxEpoch),
  8: stringToNumber(form.stakePoolTargetNum),
  9: stringToRational(form.poolPledgeInfluence),
  10: stringToRational(form.monetaryExpansion),
  11: stringToRational(form.treasuryCut),
  16: stringToNumber(form.minPoolCost),
  17: stringToNumber(form.utxoCostPerByte),
  19: {
    priceMemory: stringToRational(form.executionUnitPrices.priceMemory),
    priceSteps: stringToRational(form.executionUnitPrices.priceSteps),
  },
  20: {
    mem: stringToNumber(form.maxTxExecutionUnits.mem),
    steps: stringToNumber(form.maxTxExecutionUnits.steps),
  },
  21: {
    memory: stringToNumber(form.maxBlockExecutionUnits.memory),
    steps: stringToNumber(form.maxBlockExecutionUnits.steps),
  },
  22: stringToNumber(form.maxValueSize),
  23: stringToNumber(form.collateralPercentage),
  24: stringToNumber(form.maxCollateralInputs),
  25: {
    committeeNoConfidence: stringToRational(form.poolVotingThresholds.committeeNoConfidence),
    committeeNormal: stringToRational(form.poolVotingThresholds.committeeNormal),
    hardForkInitiation: stringToRational(form.poolVotingThresholds.hardForkInitiation),
    motionNoConfidence: stringToRational(form.poolVotingThresholds.motionNoConfidence),
    ppSecurityGroup: stringToRational(form.poolVotingThresholds.ppSecurityGroup),
  },
  26: {
    committeeNoConfidence: stringToRational(form.dRepVotingThresholds.committeeNoConfidence),
    committeeNormal: stringToRational(form.dRepVotingThresholds.committeeNormal),
    hardForkInitiation: stringToRational(form.dRepVotingThresholds.hardForkInitiation),
    motionNoConfidence: stringToRational(form.dRepVotingThresholds.motionNoConfidence),
    ppEconomicGroup: stringToRational(form.dRepVotingThresholds.ppEconomicGroup),
    ppGovernanceGroup: stringToRational(form.dRepVotingThresholds.ppGovernanceGroup),
    ppNetworkGroup: stringToRational(form.dRepVotingThresholds.ppNetworkGroup),
    ppTechnicalGroup: stringToRational(form.dRepVotingThresholds.ppTechnicalGroup),
    treasuryWithdrawal: stringToRational(form.dRepVotingThresholds.treasuryWithdrawal),
    updateConstitution: stringToRational(form.dRepVotingThresholds.updateConstitution),
  },
  27: stringToNumber(form.committeeMinSize),
  28: stringToNumber(form.committeeMaxTermLimit),
  29: stringToNumber(form.govActionLifetime),
  30: stringToNumber(form.govDeposit),
  31: stringToNumber(form.dRepDeposit),
  32: stringToNumber(form.dRepActivity),
  33: stringToNumber(form.minFeeRefScriptCoinsPerByte),
});

export const mapInitialJsonStateToCurrentJsonState = (values: InitialJsonState): CurrentJsonState => ({
  txFeePerByte: numberToCurrentFieldState(values[0]),
  txFeeFixed: numberToCurrentFieldState(values[1]),
  maxBlockBodySize: numberToCurrentFieldState(values[2]),
  maxTxSize: numberToCurrentFieldState(values[3]),
  maxBlockHeaderSize: numberToCurrentFieldState(values[4]),
  stakeAddressDeposit: numberToCurrentFieldState(values[5]),
  stakePoolDeposit: numberToCurrentFieldState(values[6]),
  poolRetireMaxEpoch: numberToCurrentFieldState(values[7]),
  stakePoolTargetNum: numberToCurrentFieldState(values[8]),
  poolPledgeInfluence: rationalToCurrentFieldState(values[9]),
  monetaryExpansion: rationalToCurrentFieldState(values[10]),
  treasuryCut: rationalToCurrentFieldState(values[11]),
  minPoolCost: numberToCurrentFieldState(values[16]),
  utxoCostPerByte: numberToCurrentFieldState(values[17]),
  maxValueSize: numberToCurrentFieldState(values[22]),
  collateralPercentage: numberToCurrentFieldState(values[23]),
  maxCollateralInputs: numberToCurrentFieldState(values[24]),
  committeeMinSize: numberToCurrentFieldState(values[27]),
  committeeMaxTermLimit: numberToCurrentFieldState(values[28]),
  govActionLifetime: numberToCurrentFieldState(values[29]),
  govDeposit: numberToCurrentFieldState(values[30]),
  dRepDeposit: numberToCurrentFieldState(values[31]),
  dRepActivity: numberToCurrentFieldState(values[32]),
  minFeeRefScriptCoinsPerByte: numberToCurrentFieldState(values[33]),
  executionUnitPrices: {
    priceMemory: rationalToCurrentFieldState(values[19].priceMemory),
    priceSteps: rationalToCurrentFieldState(values[19].priceSteps),
  },
  maxTxExecutionUnits: {
    mem: numberToCurrentFieldState(values[20].mem),
    steps: numberToCurrentFieldState(values[20].steps),
  },
  maxBlockExecutionUnits: {
    memory: numberToCurrentFieldState(values[21].memory),
    steps: numberToCurrentFieldState(values[21].steps),
  },
  poolVotingThresholds: {
    committeeNoConfidence: rationalToCurrentFieldState(values[25].committeeNoConfidence),
    committeeNormal: rationalToCurrentFieldState(values[25].committeeNormal),
    hardForkInitiation: rationalToCurrentFieldState(values[25].hardForkInitiation),
    motionNoConfidence: rationalToCurrentFieldState(values[25].motionNoConfidence),
    ppSecurityGroup: rationalToCurrentFieldState(values[25].ppSecurityGroup),
  },
  dRepVotingThresholds: {
    committeeNoConfidence: rationalToCurrentFieldState(values[26].committeeNoConfidence),
    committeeNormal: rationalToCurrentFieldState(values[26].committeeNormal),
    hardForkInitiation: rationalToCurrentFieldState(values[26].hardForkInitiation),
    motionNoConfidence: rationalToCurrentFieldState(values[26].motionNoConfidence),
    ppEconomicGroup: rationalToCurrentFieldState(values[26].ppEconomicGroup),
    ppGovernanceGroup: rationalToCurrentFieldState(values[26].ppGovernanceGroup),
    ppNetworkGroup: rationalToCurrentFieldState(values[26].ppNetworkGroup),
    ppTechnicalGroup: rationalToCurrentFieldState(values[26].ppTechnicalGroup),
    treasuryWithdrawal: rationalToCurrentFieldState(values[26].treasuryWithdrawal),
    updateConstitution: rationalToCurrentFieldState(values[26].updateConstitution),
  },
});

export const mapCurrentJsonStateToExportParams = (values: CurrentJsonState): ProposalValues => ({
  0: currentFieldStateToNumber(values.txFeePerByte),
  1: currentFieldStateToNumber(values.txFeeFixed),
  2: currentFieldStateToNumber(values.maxBlockBodySize),
  3: currentFieldStateToNumber(values.maxTxSize),
  4: currentFieldStateToNumber(values.maxBlockHeaderSize),
  5: currentFieldStateToNumber(values.stakeAddressDeposit),
  6: currentFieldStateToNumber(values.stakePoolDeposit),
  7: currentFieldStateToNumber(values.poolRetireMaxEpoch),
  8: currentFieldStateToNumber(values.stakePoolTargetNum),
  9: currentFieldStateToRational(values.poolPledgeInfluence),
  10: currentFieldStateToRational(values.monetaryExpansion),
  11: currentFieldStateToRational(values.treasuryCut),
  16: currentFieldStateToNumber(values.minPoolCost),
  17: currentFieldStateToNumber(values.utxoCostPerByte),
  19: {
    priceMemory: currentFieldStateToRational(values.executionUnitPrices.priceMemory),
    priceSteps: currentFieldStateToRational(values.executionUnitPrices.priceSteps),
  },
  20: {
    mem: currentFieldStateToNumber(values.maxTxExecutionUnits.mem),
    steps: currentFieldStateToNumber(values.maxTxExecutionUnits.steps),
  },
  21: {
    memory: currentFieldStateToNumber(values.maxBlockExecutionUnits.memory),
    steps: currentFieldStateToNumber(values.maxBlockExecutionUnits.steps),
  },
  22: currentFieldStateToNumber(values.maxValueSize),
  23: currentFieldStateToNumber(values.collateralPercentage),
  24: currentFieldStateToNumber(values.maxCollateralInputs),
  25: {
    committeeNoConfidence: currentFieldStateToRational(values.poolVotingThresholds.committeeNoConfidence),
    committeeNormal: currentFieldStateToRational(values.poolVotingThresholds.committeeNormal),
    hardForkInitiation: currentFieldStateToRational(values.poolVotingThresholds.hardForkInitiation),
    motionNoConfidence: currentFieldStateToRational(values.poolVotingThresholds.motionNoConfidence),
    ppSecurityGroup: currentFieldStateToRational(values.poolVotingThresholds.ppSecurityGroup),
  },
  26: {
    committeeNoConfidence: currentFieldStateToRational(values.dRepVotingThresholds.committeeNoConfidence),
    committeeNormal: currentFieldStateToRational(values.dRepVotingThresholds.committeeNormal),
    hardForkInitiation: currentFieldStateToRational(values.dRepVotingThresholds.hardForkInitiation),
    motionNoConfidence: currentFieldStateToRational(values.dRepVotingThresholds.motionNoConfidence),
    ppEconomicGroup: currentFieldStateToRational(values.dRepVotingThresholds.ppEconomicGroup),
    ppGovernanceGroup: currentFieldStateToRational(values.dRepVotingThresholds.ppGovernanceGroup),
    ppNetworkGroup: currentFieldStateToRational(values.dRepVotingThresholds.ppNetworkGroup),
    ppTechnicalGroup: currentFieldStateToRational(values.dRepVotingThresholds.ppTechnicalGroup),
    treasuryWithdrawal: currentFieldStateToRational(values.dRepVotingThresholds.treasuryWithdrawal),
    updateConstitution: currentFieldStateToRational(values.dRepVotingThresholds.updateConstitution),
  },
  27: currentFieldStateToNumber(values.committeeMinSize),
  28: currentFieldStateToNumber(values.committeeMaxTermLimit),
  29: currentFieldStateToNumber(values.govActionLifetime),
  30: currentFieldStateToNumber(values.govDeposit),
  31: currentFieldStateToNumber(values.dRepDeposit),
  32: currentFieldStateToNumber(values.dRepActivity),
  33: currentFieldStateToNumber(values.minFeeRefScriptCoinsPerByte),
});

export const mapPostValuesToInitialJsonValues = (result: any): InitialJsonState => ({
  0: result.txFeePerByte.value,
  1: result.txFeeFixed.value,
  2: result.maxBlockBodySize.value,
  3: result.maxTxSize.value,
  4: result.maxBlockHeaderSize.value,
  5: result.stakeAddressDeposit.value,
  6: result.stakePoolDeposit.value,
  7: result.poolRetireMaxEpoch.value,
  8: result.stakePoolTargetNum.value,
  9: result.poolPledgeInfluence.value,
  10: result.monetaryExpansion.value,
  11: result.treasuryCut.value,
  16: result.minPoolCost.value,
  17: result.utxoCostPerByte.value,
  19: {
    priceMemory: result.executionUnitPrices.priceMemory.value,
    priceSteps: result.executionUnitPrices.priceSteps.value,
  },
  20: {
    mem: result.maxTxExecutionUnits.mem.value,
    steps: result.maxTxExecutionUnits.steps.value,
  },
  21: {
    memory: result.maxBlockExecutionUnits.memory.value,
    steps: result.maxBlockExecutionUnits.steps.value,
  },
  22: result.maxValueSize.value,
  23: result.collateralPercentage.value,
  24: result.maxCollateralInputs.value,
  25: {
    committeeNoConfidence: result.poolVotingThresholds.committeeNoConfidence.value,
    committeeNormal: result.poolVotingThresholds.committeeNormal.value,
    hardForkInitiation: result.poolVotingThresholds.hardForkInitiation.value,
    motionNoConfidence: result.poolVotingThresholds.motionNoConfidence.value,
    ppSecurityGroup: result.poolVotingThresholds.ppSecurityGroup.value,
  },
  26: {
    committeeNoConfidence: result.dRepVotingThresholds.committeeNoConfidence.value,
    committeeNormal: result.dRepVotingThresholds.committeeNormal.value,
    hardForkInitiation: result.dRepVotingThresholds.hardForkInitiation.value,
    motionNoConfidence: result.dRepVotingThresholds.motionNoConfidence.value,
    ppEconomicGroup: result.dRepVotingThresholds.ppEconomicGroup.value,
    ppGovernanceGroup: result.dRepVotingThresholds.ppGovernanceGroup.value,
    ppNetworkGroup: result.dRepVotingThresholds.ppNetworkGroup.value,
    ppTechnicalGroup: result.dRepVotingThresholds.ppTechnicalGroup.value,
    treasuryWithdrawal: result.dRepVotingThresholds.treasuryWithdrawal.value,
    updateConstitution: result.dRepVotingThresholds.updateConstitution.value,
  },
  27: result.committeeMinSize.value,
  28: result.committeeMaxTermLimit.value,
  29: result.govActionLifetime.value,
  30: result.govDeposit.value,
  31: result.dRepDeposit.value,
  32: result.dRepActivity.value,
  33: result.minFeeRefScriptCoinsPerByte.value,
});